// ─────────────────────────────────────────────
//  DoctorAssignFlow — what happens when the AIC clicks "Assign" on an
//  unassigned DOCTOR (mirrors BenchAssignFlow for CRNAs):
//
//    • New Room  → parent opens the Add Assignment modal in doctor mode
//                  (Room + MD only, MD pre-filled with this doctor).
//    • Takeover  → pick up to 4 OCCUPIED rooms (a doctor can cover ≤4). On
//                  submit, the new doctor is STAGED on each room: their name
//                  blinks below the current supervisor for 5 minutes, then the
//                  old supervisor drops and the new doctor takes that place.
//    • Home      → mark the doctor departed for the day.
//    • Back to MD Call (only when the doctor was X-removed from the call
//      list and is pending Home) → restore them to MD Call on every board.
// ─────────────────────────────────────────────
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useToast } from "../context/ToastContext";
import {
  updateAssignmentRow,
  setRosterMemberDeparted,
} from "../firebase/firestore";
import { assignmentForRoom } from "../domain/gridTemplate";
import tokens from "../theme/tokens";

const { color, radius, shadow, font, space } = tokens;

const MAX_ROOMS = 4;

export default function DoctorAssignFlow({ doctor, rooms = [], assignments = [], facilityId, date, updatedBy, onNewRoom, onClose, mdCallPending = null, onBackToCall = null, onAfterHome = null, onHomeIntercept = null }) {
  const toast = useToast();
  const [step, setStep] = useState("choose"); // choose | takeover
  const [picks, setPicks] = useState(["", "", "", ""]); // up to 4 room ids
  const [busy, setBusy] = useState(false);

  if (!doctor) return null;

  const close = () => { setStep("choose"); setPicks(["", "", "", ""]); onClose(); };

  const sendHome = async () => {
    // Sending the current AIC home is intercepted by the parent's handoff
    // modal (a successor must be named first) — this chooser steps aside.
    if (onHomeIntercept && onHomeIntercept(doctor)) { close(); return; }
    setBusy(true);
    try {
      await setRosterMemberDeparted(facilityId, date, doctor.id, true, updatedBy);
      // An X-removed doctor going home also confirms the MD-call removal on
      // every board — otherwise a stale "Back" could restore a departed MD.
      if (mdCallPending && onAfterHome) await onAfterHome(mdCallPending);
      toast.success(`${doctor.name} sent home for the day.`);
      close();
    } catch (e) {
      console.error("Send doctor home failed:", e);
      toast.error("Could not send them home.");
      setBusy(false);
    }
  };

  const backToCall = async () => {
    if (!mdCallPending || !onBackToCall) return;
    setBusy(true);
    try {
      await onBackToCall(mdCallPending);
      close();
    } catch (e) {
      console.error("Back to MD Call failed:", e);
      toast.error("Could not restore them to MD Call.");
      setBusy(false);
    }
  };

  // Occupied rooms = today's assignment rows that have a supervising doctor,
  // DEDUPED to one entry per room (a room can have more than one assignment doc).
  const occupied = Array.from(
    new Map(
      assignments
        .filter((a) => a.attendingId && a.attendingName && a.orRoom)
        .map((a) => [a.orRoom, a])
    ).values()
  ).sort((a, b) => (a.orRoom || "").localeCompare(b.orRoom || "", undefined, { numeric: true }));

  const setPick = (i, val) => setPicks((p) => p.map((v, idx) => (idx === i ? val : v)));

  const chosenRooms = Array.from(new Set(picks.filter(Boolean)));

  const doTakeover = async () => {
    if (chosenRooms.length === 0) return;
    setBusy(true);
    try {
      const at = Date.now();
      for (const orRoom of chosenRooms) {
        const row = occupied.find((a) => a.orRoom === orRoom) || assignmentForRoom(orRoom, assignments);
        if (!row?.id) continue;
        // Stage the handoff: new doctor blinks under the supervisor for 5 min,
        // then RoomCard swaps them in (mdTakeover* fields).
        await updateAssignmentRow(row.id, {
          mdTakeoverId: doctor.id,
          mdTakeoverName: doctor.name,
          mdTakeoverAt: at,
        });
      }
      toast.success(`${doctor.name} taking over ${chosenRooms.length} room${chosenRooms.length === 1 ? "" : "s"} (5-min handoff).`);
      close();
    } catch (e) {
      console.error("Doctor takeover failed:", e);
      toast.error("Could not start the takeover.");
      setBusy(false);
    }
  };

  // PORTAL to <body> (same as Modal/SoloReliefFlow): in TV mode the board sits
  // under transform:scale(...) — a transformed ancestor becomes the containing
  // block for position:fixed, which shrank/shifted this chooser with the board
  // scale and made its options unclickable on real TVs ("can't choose").
  return createPortal(
    <div style={st.overlay} role="dialog" aria-modal="true">
      <div style={st.modal}>
        {step === "choose" && (
          <>
            <div style={st.title}>Assign {doctor.name}</div>
            <div style={st.sub}>Doctor · choose how to place them.</div>
            <div style={st.choiceCol}>
              {/* Only offered when this doctor was X-removed from MD Call and
                  is pending Home — one tap puts them back on every board. */}
              {mdCallPending && onBackToCall && (
                <button style={{ ...st.choice, borderColor: color.accent.primary }} disabled={busy} onClick={backToCall}>
                  <span style={st.choiceIcon}>📟</span>
                  <span><span style={st.choiceLabel}>Back to MD Call</span><span style={st.choiceSub}>Restore them to the MD Call roster on all facilities.</span></span>
                </button>
              )}
              <button style={st.choice} disabled={busy} onClick={() => onNewRoom(doctor.id)}>
                <span style={st.choiceIcon}>🚪</span>
                <span><span style={st.choiceLabel}>New Room</span><span style={st.choiceSub}>Place in a room — pick the room (MD is this doctor).</span></span>
              </button>
              <button style={st.choice} disabled={busy} onClick={() => setStep("takeover")}>
                <span style={st.choiceIcon}>🔁</span>
                <span><span style={st.choiceLabel}>Takeover</span><span style={st.choiceSub}>Take over up to 4 occupied rooms (5-min handoff).</span></span>
              </button>
              <button style={st.choice} disabled={busy} onClick={sendHome}>
                <span style={st.choiceIcon}>🏠</span>
                <span><span style={st.choiceLabel}>Home</span><span style={st.choiceSub}>Send them home — moves to the Home section.</span></span>
              </button>
            </div>
            <div style={st.actions}><button style={st.ghost} onClick={close} disabled={busy}>Cancel</button></div>
          </>
        )}

        {step === "takeover" && (
          <>
            <div style={st.title}>Takeover — pick rooms</div>
            <div style={st.sub}>{doctor.name} will take over the chosen rooms (up to {MAX_ROOMS}). Their name blinks under the current supervisor for 5 minutes, then replaces them.</div>
            {occupied.length === 0 ? (
              <div style={st.empty}>No occupied rooms to take over yet.</div>
            ) : (
              <div style={st.pickCol}>
                {picks.map((val, i) => (
                  <select key={i} style={st.select} value={val} onChange={(e) => setPick(i, e.target.value)} aria-label={`Room ${i + 1}`}>
                    <option value="">Room {i + 1} — none</option>
                    {occupied.map((a) => (
                      <option key={a.id} value={a.orRoom}
                        disabled={picks.includes(a.orRoom) && picks[i] !== a.orRoom}>
                        {a.orRoom} — Dr. {(a.attendingName || "").split(" ").slice(-1)[0]}
                      </option>
                    ))}
                  </select>
                ))}
              </div>
            )}
            <div style={st.actions}>
              <button style={st.ghost} onClick={() => setStep("choose")} disabled={busy}>← Back</button>
              <button style={st.primary} onClick={doTakeover} disabled={busy || chosenRooms.length === 0}>
                {busy ? "Starting…" : `Take over ${chosenRooms.length || ""} room${chosenRooms.length === 1 ? "" : "s"}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

const st = {
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,.40)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 250, padding: 16 },
  modal: { background: color.surface.card, border: `1px solid ${color.border.default}`, boxShadow: shadow.lg, borderRadius: radius.lg, padding: 24, width: 440, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto", fontFamily: font.family },
  title: { fontSize: font.size.heading, fontWeight: font.weight.bold, color: color.text.primary, marginBottom: 4 },
  sub: { fontSize: 13, color: color.text.muted, marginBottom: 18, lineHeight: 1.5 },
  choiceCol: { display: "flex", flexDirection: "column", gap: space.sm },
  choice: { display: "flex", alignItems: "center", gap: space.md, textAlign: "left", width: "100%", background: color.surface.raised, border: `1px solid ${color.border.default}`, borderRadius: radius.md, padding: "14px 16px", cursor: "pointer", fontFamily: "inherit" },
  choiceIcon: { fontSize: 24, flexShrink: 0 },
  choiceLabel: { display: "block", fontSize: 15, fontWeight: font.weight.bold, color: color.text.primary },
  choiceSub: { display: "block", fontSize: font.size.label, color: color.text.muted, marginTop: 2 },
  pickCol: { display: "flex", flexDirection: "column", gap: space.sm },
  select: { width: "100%", boxSizing: "border-box", background: color.surface.raised, border: `1px solid ${color.border.default}`, color: color.text.primary, borderRadius: radius.sm, padding: "9px 12px", fontSize: 13, fontFamily: font.family },
  empty: { fontSize: 13, color: color.text.muted, padding: "12px 4px" },
  actions: { display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18 },
  ghost: { background: "transparent", border: `1px solid ${color.border.default}`, color: color.text.secondary, borderRadius: radius.sm, padding: "8px 14px", fontSize: 13, cursor: "pointer" },
  primary: { background: color.accent.primary, color: "#fff", border: "none", borderRadius: radius.sm, padding: "8px 14px", fontSize: 13, fontWeight: font.weight.bold, cursor: "pointer" },
};
